import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellSlash, X, CheckCircle, PaperPlaneTilt, Trash, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import HushPlusUpgrade from "@/components/HushPlusUpgrade";
import ReferralCard from "@/components/ReferralCard";
import {
  pushCapability,
  isSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from "@/lib/push";

/**
 * SettingsDrawer — manage daily reminder preferences (enable + local time)
 * and web-push subscription.
 */
export default function SettingsDrawer({ open, onClose, user, onSaved }) {
  const [enabled, setEnabled] = useState(user?.reminder_enabled || false);
  const [time, setTime] = useState(user?.reminder_time || "20:00");
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(0);
  const [testStatus, setTestStatus] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setEnabled(user?.reminder_enabled || false);
      setTime(user?.reminder_time || "20:00");
      setErr("");
      setTestStatus("");
      (async () => setSubscribed(await isSubscribed()))();
    }
  }, [open, user]);

  const handleSubscribe = async () => {
    setErr("");
    setPushBusy(true);
    try {
      await subscribeToPush();
      setPermission(Notification.permission);
      setSubscribed(true);
    } catch (e) {
      setErr(e?.message || "Couldn't enable push notifications.");
    } finally {
      setPushBusy(false);
    }
  };

  const handleUnsubscribe = async () => {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
    } catch (err) {
      console.warn("[SettingsDrawer] unsubscribe failed:", err);
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    setTestStatus("Sending…");
    try {
      await sendTestPush();
      setTestStatus("Sent — check your notifications.");
    } catch (e) {
      setTestStatus("Couldn't send test push.");
    }
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const tzOffsetMin = -new Date().getTimezoneOffset();
      await api.put("/user/preferences", {
        reminder_enabled: enabled,
        reminder_time: time,
        reminder_tz_offset_min: tzOffsetMin,
      });
      setSavedAt(Date.now());
      onSaved?.({
        reminder_enabled: enabled,
        reminder_time: time,
        reminder_tz_offset_min: tzOffsetMin,
      });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const pushSupported = pushCapability();

  return (
    <div className="fixed inset-0 z-50" data-testid="settings-drawer">
      <div className="absolute inset-0 bg-ink-900/30 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-bone-100 border-l border-bone-300 shadow-2xl overflow-y-auto animate-fade-up">
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="eyebrow">Settings</p>
              <h2 className="font-serif text-2xl mt-1">Daily reminder</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-bone-200 text-ink-500"
              data-testid="settings-close-btn"
              aria-label="Close settings"
            >
              <X size={18} />
            </button>
          </div>

          {/* Hush Plus — first card so monetization is the most visible row */}
          <div className="mb-5">
            <HushPlusUpgrade user={user} />
          </div>

          {/* Referral — invite friends, earn free Plus on both sides */}
          <ReferralCard onChanged={async () => {
            try { const { data } = await api.get("/auth/me"); setUser(data); } catch (_) { /* noop */ }
          }} />

          <div className="card p-5 mb-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-serif text-lg">A gentle nudge each day</p>
                <p className="text-sm text-ink-500 mt-1">Email and/or browser notification at your chosen time.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="sr-only peer"
                  data-testid="reminder-enabled-toggle"
                />
                <div className="w-11 h-6 bg-bone-300 rounded-full peer peer-checked:bg-brand after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
              </label>
            </div>

            {enabled && (
              <div className="mt-5 pt-5 border-t border-bone-300 space-y-4">
                <div>
                  <label className="eyebrow block mb-2">Reminder time (your local time)</label>
                  <input
                    type="time"
                    className="input"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    data-testid="reminder-time-input"
                  />
                  <p className="text-xs text-ink-500 mt-2">Email will arrive within {5} minutes of this time.</p>
                </div>
              </div>
            )}
          </div>

          {/* Push notifications card */}
          <div className="card p-5 mb-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-serif text-lg flex items-center gap-2">
                  <Bell size={18} weight={subscribed ? "fill" : "regular"} className={subscribed ? "text-brand" : "text-ink-500"} />
                  Browser push
                </p>
                <p className="text-sm text-ink-500 mt-1">
                  {!pushSupported
                    ? "Not supported in this browser."
                    : permission === "denied"
                    ? "Blocked. Enable in your browser site settings."
                    : subscribed
                    ? "Enabled — you'll get a push notification at reminder time."
                    : "Off. Tap enable to receive a system notification."}
                </p>
              </div>
              {pushSupported && permission !== "denied" && (
                <>
                  {subscribed ? (
                    <button
                      onClick={handleUnsubscribe}
                      disabled={pushBusy}
                      className="btn-ghost text-xs py-2 px-3"
                      data-testid="push-unsubscribe-btn"
                    >
                      {pushBusy ? "…" : "Turn off"}
                    </button>
                  ) : (
                    <button
                      onClick={handleSubscribe}
                      disabled={pushBusy}
                      className="btn-primary text-xs py-2 px-3 flex items-center gap-1.5"
                      data-testid="push-subscribe-btn"
                    >
                      <Bell size={14} /> {pushBusy ? "Enabling…" : "Enable"}
                    </button>
                  )}
                </>
              )}
              {permission === "denied" && <BellSlash size={20} className="text-ink-500" />}
            </div>

            {subscribed && (
              <div className="mt-4 pt-4 border-t border-bone-300 flex items-center justify-between gap-3">
                <p className="text-xs text-ink-500">{testStatus || "Send yourself a test push to verify."}</p>
                <button
                  onClick={handleTestPush}
                  className="btn-ghost text-xs py-2 px-3 flex items-center gap-1.5"
                  data-testid="push-test-btn"
                >
                  <PaperPlaneTilt size={14} /> Test
                </button>
              </div>
            )}
          </div>

          {err && (
            <div className="text-sm text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2 mb-4" data-testid="settings-error">
              {err}
            </div>
          )}

          <button
            onClick={save}
            disabled={saving}
            className="btn-primary w-full"
            data-testid="settings-save-btn"
          >
            {saving ? "Saving…" : "Save preferences"}
          </button>

          {savedAt > 0 && (
            <p className="text-xs text-moss text-center mt-3 flex items-center justify-center gap-1" data-testid="settings-saved-toast">
              <CheckCircle size={14} weight="fill" /> Saved
            </p>
          )}

          {/* Danger zone — Delete account */}
          <div className="mt-10 pt-8 border-t border-bone-300">
            <p className="eyebrow mb-3">Danger zone</p>
            {!deleteOpen ? (
              <button
                onClick={() => setDeleteOpen(true)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md border border-bone-300 hover:border-brand/40 hover:bg-brand/5 text-left transition-all"
                data-testid="open-delete-account-btn"
              >
                <div>
                  <p className="font-medium text-ink-900 text-sm">Delete my account</p>
                  <p className="text-xs text-ink-500 mt-0.5">Permanently remove every entry, session, and reminder.</p>
                </div>
                <Trash size={18} className="text-ink-500" />
              </button>
            ) : (
              <div className="border border-brand/40 bg-brand/5 rounded-md p-4" data-testid="delete-account-confirm">
                <div className="flex items-start gap-2 mb-3">
                  <Warning size={18} weight="fill" className="text-brand shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-ink-900 text-sm">This cannot be undone.</p>
                    <p className="text-xs text-ink-500 mt-1">
                      All your entries, transcripts, and mood data will be permanently deleted. Type{" "}
                      <code className="bg-bone-200 px-1 rounded">DELETE</code> to confirm.
                    </p>
                  </div>
                </div>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="input mt-2"
                  placeholder="Type DELETE"
                  data-testid="delete-confirm-input"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => { setDeleteOpen(false); setDeleteConfirm(""); }}
                    className="btn-ghost text-sm flex-1 py-2"
                    data-testid="delete-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (deleteConfirm !== "DELETE" || deleting) return;
                      setDeleting(true);
                      try {
                        await api.delete("/auth/account");
                        setUser(null);
                        navigate("/", { replace: true });
                      } catch (e) {
                        setErr(formatApiError(e));
                        setDeleting(false);
                      }
                    }}
                    disabled={deleteConfirm !== "DELETE" || deleting}
                    className="text-sm flex-1 py-2 px-3 rounded-full bg-brand text-bone-100 hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    data-testid="delete-confirm-btn"
                  >
                    {deleting ? "Deleting…" : "Yes, delete everything"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
